import {
  useEffect,
  useState
} from 'react';
import {
  DesignModuleNode,
  LanguageCode,
  PromptAudioUrls,
  TextToSpeechVoiceOrDefault
} from '@app/types';

export type UseAudioUrlsProps = {
  isDynamicPrompt: boolean;
  languageCode: LanguageCode;
  node: DesignModuleNode;
  onUrlChange?: (newValue: PromptAudioUrls | null) => void;
  ttsVoice?: TextToSpeechVoiceOrDefault;
};

type AudioEntryUpdate = {
  lockedUrl?: string | null;
  url?: string | null;
};

/**
 * Owns the preview (`url`) & approved (`lockedUrl`) audio for a single prompt/language/voice.
 *
 * Static prompts with a TTS voice persist onto the design config; dynamic prompts keep only an
 * ephemeral preview URL (locking is not supported). Persistence details live here so the
 * orchestrating hook only deals with the resolved `url`/`lockedUrl` & the setters.
 */
export function useAudioUrls({
  isDynamicPrompt,
  languageCode,
  node,
  onUrlChange,
  ttsVoice
}: UseAudioUrlsProps) {
  const [ephemeralUrl, setEphemeralUrl] = useState<string | null>(null);

  const audioUrls: PromptAudioUrls = node.props?.config?.audioUrls ?? {};
  const localizedUrls = audioUrls[languageCode] ?? {};
  const persistAudioUrls = !isDynamicPrompt && !!ttsVoice;
  const {
    lockedUrl: persistedLockedUrl = null,
    url: persistedUrl = null
  } = ((persistAudioUrls) ? localizedUrls[ttsVoice!] : undefined) ?? {};

  const lockedUrl = (persistAudioUrls) ? persistedLockedUrl : null;
  const url = (persistAudioUrls) ? persistedUrl : ephemeralUrl;

  useEffect(() => {
    setEphemeralUrl(null);
  }, [isDynamicPrompt, languageCode, ttsVoice]);

  const updateEntry = ({
    lockedUrl: nextLockedUrl,
    url: nextUrl
  }: AudioEntryUpdate) => {
    if (!persistAudioUrls) {
      if (nextUrl !== undefined) {
        setEphemeralUrl(nextUrl);
      }
      return;
    }
    if (!ttsVoice) {
      return;
    }
    const copyAudioUrls: PromptAudioUrls = {
      ...audioUrls,
      [languageCode]: { ...(audioUrls[languageCode] ?? {}) }
    };
    const {
      lockedUrl: currentLockedUrl,
      url: currentUrl
    } = copyAudioUrls[languageCode]?.[ttsVoice] ?? {};
    const resolvedUrl = (nextUrl === undefined) ? currentUrl ?? null : nextUrl;
    const resolvedLockedUrl = (nextLockedUrl === undefined) ? currentLockedUrl ?? null : nextLockedUrl;
    if (!resolvedUrl && !resolvedLockedUrl && copyAudioUrls[languageCode]?.[ttsVoice]) {
      delete copyAudioUrls[languageCode]![ttsVoice];
    } else if (resolvedUrl || resolvedLockedUrl) {
      copyAudioUrls[languageCode] = {
        ...copyAudioUrls[languageCode],
        [ttsVoice]: {
          ...(resolvedLockedUrl ? { lockedUrl: resolvedLockedUrl } : {}),
          ...(resolvedUrl ? { url: resolvedUrl } : {})
        }
      };
    }
    if (copyAudioUrls[languageCode] && Object.keys(copyAudioUrls[languageCode]).length === 0) {
      delete copyAudioUrls[languageCode];
    }
    onUrlChange?.((Object.keys(copyAudioUrls).length > 0) ? copyAudioUrls : null);
  };

  const setUrl = (newUrl: string | null) => {
    if (newUrl !== url) {
      updateEntry({ url: newUrl });
    }
  };

  const setLockedUrl = (newLockedUrl: string | null) => {
    if (!persistAudioUrls) {
      return;
    }
    if (newLockedUrl !== lockedUrl) {
      updateEntry({ lockedUrl: newLockedUrl });
    }
  };

  return {
    lockedUrl,
    persistAudioUrls,
    setLockedUrl,
    setUrl,
    updateEntry,
    url
  };
}
